/**
 * What each plan includes, and which plan's limits apply to a business.
 *
 * This mirrors the SQL function public.org_plan_limits (migration
 * 20261101213644_org_plan_limits.sql), which is what the database actually
 * enforces. Keep the two in step.
 *
 * - Solo (starter): 1 team member, no staff logins, no custom domain, no texts.
 * - Team (pro): up to 5 team members, staff logins, custom domain, texts, AI logo maker.
 * - Business: unlimited team members, staff logins, custom domain, texts, AI
 *   logo maker and AI proposal writer, and can switch off the "Powered by"
 *   footer credit (public.org_shows_powered_by).
 *
 * Texts (SMS) go through the business's own Twilio account, so they pay
 * Twilio directly; the plan only decides whether the feature is switched on.
 * The AI flags were added in migration 20261102124313_ai_proposals_and_logos.sql.
 *
 * A business with a Stripe subscription (or a row set to active by hand) gets
 * its chosen plan. Everyone else is on the free trial and gets Team limits.
 * Downgrades never delete anything: existing members keep working, but nothing
 * new can be added while the business is over its limit.
 */

export type PlanKey = 'starter' | 'pro' | 'business';

export interface PlanFeatures {
  /** null means unlimited. */
  maxTeamMembers: number | null;
  staffLogins: boolean;
  customDomain: boolean;
  /** Text messages through the business's own Twilio account. */
  sms: boolean;
  /** "Draft with AI" on quotes. */
  aiProposalWriter: boolean;
  /** "Create a logo with AI" on Company Info. */
  aiLogoMaker: boolean;
  /** Can switch off "Powered by The Polished Version" in the site footer. */
  hidePoweredBy: boolean;
}

export const PLAN_LIMITS: Record<PlanKey, PlanFeatures> = {
  starter: { maxTeamMembers: 1, staffLogins: false, customDomain: false, sms: false, aiProposalWriter: false, aiLogoMaker: false, hidePoweredBy: false },
  pro: { maxTeamMembers: 5, staffLogins: true, customDomain: true, sms: true, aiProposalWriter: false, aiLogoMaker: true, hidePoweredBy: false },
  business: { maxTeamMembers: null, staffLogins: true, customDomain: true, sms: true, aiProposalWriter: true, aiLogoMaker: true, hidePoweredBy: true },
};

/** The lowest plan that includes a feature, for upgrade prompts. */
export function lowestPlanWith(feature: 'aiProposalWriter' | 'aiLogoMaker' | 'staffLogins' | 'customDomain'): PlanKey {
  return (['starter', 'pro', 'business'] as const).find((plan) => PLAN_LIMITS[plan][feature]) ?? 'business';
}

/** The names on the pricing page. */
export const PLAN_NAME: Record<PlanKey, string> = {
  starter: 'Solo',
  pro: 'Team',
  business: 'Business',
};

/** Limits that apply during the free trial: Team, so businesses can try everything. */
export const TRIAL_PLAN: PlanKey = 'pro';

export interface PlanLimits extends PlanFeatures {
  /** The plan whose limits apply right now. */
  effectivePlan: PlanKey;
  /** The plan on the subscription row (may differ during a trial). */
  chosenPlan: PlanKey | null;
  onTrial: boolean;
}

export interface PlanSubscriptionLike {
  plan: string | null;
  status: string | null;
  stripe_subscription_id: string | null;
}

export function isPlanKey(value: unknown): value is PlanKey {
  return value === 'starter' || value === 'pro' || value === 'business';
}

export function planName(plan: string | null | undefined): string {
  return isPlanKey(plan) ? PLAN_NAME[plan] : 'your plan';
}

/** Same rules as public.org_plan_limits_internal. */
export function getPlanLimits(sub: PlanSubscriptionLike | null | undefined): PlanLimits {
  const chosenPlan = isPlanKey(sub?.plan) ? sub!.plan as PlanKey : null;
  const subscribed = Boolean(
    sub && (sub.stripe_subscription_id || sub.status === 'active' || sub.status === 'past_due'),
  );
  // An unknown plan key on a paid row falls back to the trial plan rather than locking anyone out.
  const effectivePlan: PlanKey = subscribed && chosenPlan ? chosenPlan : TRIAL_PLAN;
  return { ...PLAN_LIMITS[effectivePlan], effectivePlan, chosenPlan, onTrial: !subscribed };
}

/** A row returned by the org_plan_limits RPC. */
export interface PlanLimitsRow {
  effective_plan: string;
  chosen_plan: string | null;
  on_trial: boolean;
  max_team_members: number | null;
  staff_logins: boolean;
  custom_domain: boolean;
  team_member_count: number;
  /** Missing before migration 20261102124317_sms_twilio. */
  sms?: boolean | null;
  /** Missing until the AI migration is applied: fall back to the plan's defaults. */
  ai_proposal_writer?: boolean | null;
  ai_logo_maker?: boolean | null;
}

export function planLimitsFromRow(row: PlanLimitsRow): PlanLimits & { teamMemberCount: number } {
  const effectivePlan = isPlanKey(row.effective_plan) ? row.effective_plan : TRIAL_PLAN;
  return {
    effectivePlan,
    chosenPlan: isPlanKey(row.chosen_plan) ? row.chosen_plan : null,
    onTrial: Boolean(row.on_trial),
    maxTeamMembers: row.max_team_members ?? null,
    staffLogins: Boolean(row.staff_logins),
    customDomain: Boolean(row.custom_domain),
    // Older databases without the column: same rule as the SQL (not Solo).
    sms: row.sms == null ? effectivePlan !== 'starter' : Boolean(row.sms),
    aiProposalWriter: row.ai_proposal_writer == null ? PLAN_LIMITS[effectivePlan].aiProposalWriter : Boolean(row.ai_proposal_writer),
    aiLogoMaker: row.ai_logo_maker == null ? PLAN_LIMITS[effectivePlan].aiLogoMaker : Boolean(row.ai_logo_maker),
    hidePoweredBy: effectivePlan === 'business',
    teamMemberCount: Number(row.team_member_count ?? 0),
  };
}

/** The next plan up that has room for more team members, if any. */
export function upgradePlanFor(plan: PlanKey): PlanKey | null {
  if (plan === 'starter') return 'pro';
  if (plan === 'pro') return 'business';
  return null;
}

const members = (n: number) => (n === 1 ? '1 team member' : `${n} team members`);

/** "1 team member", "Up to 5 team members", "Unlimited team members". */
export function teamMemberLimitLabel(features: PlanFeatures): string {
  if (features.maxTeamMembers === null) return 'Unlimited team members';
  if (features.maxTeamMembers === 1) return '1 team member';
  return `Up to ${features.maxTeamMembers} team members`;
}

/** What a plan includes, for the plan cards on the Billing page. */
export function planIncludes(plan: PlanKey): { label: string; included: boolean }[] {
  const f = PLAN_LIMITS[plan];
  return [
    { label: teamMemberLimitLabel(f), included: true },
    { label: f.staffLogins ? 'Staff logins' : 'No staff logins', included: f.staffLogins },
    { label: f.customDomain ? 'Connect a domain you own' : 'Free subdomain only', included: f.customDomain },
    { label: f.sms ? 'Text message reminders (your own Twilio)' : 'No text messages', included: f.sms },
    { label: 'AI logo maker', included: f.aiLogoMaker },
    { label: 'AI proposal writer', included: f.aiProposalWriter },
  ];
}

export interface TeamAllowance {
  count: number;
  max: number | null;
  /** True when one more team member can be added. */
  canAdd: boolean;
  atLimit: boolean;
  overLimit: boolean;
  /** "3 of 5 team members", "4 team members (1 included)", or "3 team members" when unlimited. */
  usageLabel: string;
  /** Plain notice to show when the business can't add anyone, else null. */
  notice: string | null;
  /** Plan to suggest upgrading to, or null when there is nothing higher. */
  upgradeTo: PlanKey | null;
}

export function getTeamAllowance(limits: PlanLimits, count: number): TeamAllowance {
  const max = limits.maxTeamMembers;
  const name = PLAN_NAME[limits.effectivePlan];
  const upgradeTo = upgradePlanFor(limits.effectivePlan);
  const usageLabel =
    max === null ? members(count) : count > max ? `${members(count)} (${max} included)` : `${count} of ${members(max)}`;

  if (max === null) {
    return { count, max, canAdd: true, atLimit: false, overLimit: false, usageLabel, notice: null, upgradeTo };
  }

  const included = max === 1 ? '1' : `up to ${max}`;
  const upgrade = upgradeTo ? ` Upgrade to ${PLAN_NAME[upgradeTo]} to add more.` : '';
  if (count > max) {
    return {
      count, max, canAdd: false, atLimit: false, overLimit: true, usageLabel, upgradeTo,
      notice: `You have ${members(count)}; ${name} includes ${included}. Remove members or upgrade.`,
    };
  }
  if (count === max) {
    return {
      count, max, canAdd: false, atLimit: true, overLimit: false, usageLabel, upgradeTo,
      notice: `${limits.onTrial ? 'Your free trial' : `Your ${name} plan`} includes ${included === '1' ? '1 team member' : `${included} team members`}.${upgrade}`,
    };
  }
  return { count, max, canAdd: true, atLimit: false, overLimit: false, usageLabel, notice: null, upgradeTo };
}
